/**
 * The name cell of a roster row: the expand control, the drill link, and a
 * quiet second line (BON-534).
 *
 * The design puts a mono line under every region name ("NE · 3 metros"). We
 * have no short codes, so the slug stands in — and a region with children says
 * how many. EVERY row gets the line, including leaves: without it the rows
 * collapse to one line and the table reads flatter and tighter than drawn.
 *
 * The two are siblings, never nested — see `ScopeCaret`. A region with nothing
 * beneath it still reserves the caret's width, so names stay on one vertical
 * line whether or not their neighbours expand.
 */
import { Link } from 'react-router-dom';

import { ScopeCaret } from '../../primitives/scope/ScopeCaret';

import type { OverviewRegionRow } from './overview-types';

/** A region's own overview, addressed by its full ancestor path. */
const overviewHref = (row: OverviewRegionRow): string => `/admin/overview/${row.path.join('/')}`;

export interface RegionRosterNameProps {
  row: OverviewRegionRow;
  /** Indent relative to the roster's own top level, not the hierarchy's. */
  indent: number;
  expandable: boolean;
  open: boolean;
  onToggle: () => void;
  /** Children present IN THIS RESPONSE, so the line never promises rows the
   *  roster cannot show at the requested depth. */
  subCount: number;
}

/** "new-england · 5 sub-regions", or just the slug for a leaf. */
function secondLine(slug: string, subCount: number): string {
  if (subCount === 0) return slug;
  return `${slug} · ${subCount} ${subCount === 1 ? 'sub-region' : 'sub-regions'}`;
}

export function RegionRosterName({
  row,
  indent,
  expandable,
  open,
  onToggle,
  subCount,
}: RegionRosterNameProps) {
  return (
    <span
      style={{
        display: 'flex',
        // The cell is two lines tall; the caret belongs beside the NAME rather
        // than floating at the vertical middle of both lines.
        alignItems: 'flex-start',
        gap: 6,
        minWidth: 0,
        paddingTop: 7,
        paddingBottom: 7,
        paddingLeft: indent * 18,
      }}
    >
      {expandable ? (
        <ScopeCaret open={open} regionName={row.name} onToggle={onToggle} />
      ) : (
        <span style={{ flex: '0 0 18px' }} aria-hidden="true" />
      )}
      <Link
        to={overviewHref(row)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          minWidth: 0,
          color: 'var(--nb-ink)',
          textDecoration: 'none',
        }}
      >
        <span
          style={{
            fontWeight: indent === 0 ? 700 : 600,
            fontSize: indent === 0 ? 13 : 12.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.name}
        </span>
        <span
          className="nb-mono"
          style={{
            fontSize: 10.5,
            color: 'var(--nb-subtle)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {secondLine(row.slug, subCount)}
        </span>
      </Link>
    </span>
  );
}
